import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import RBACService from '@/services/rbac';
import { useUserStore } from './user';

export const useRBACStore = defineStore('rbac', () => {
  const userStore = useUserStore();
  const service = new RBACService();

  const roles = ref([]);
  const permissions = ref([]);
  const users = ref([]);
  const rolePermissions = ref({});
  const roleTags = ref({});
  const settings = ref({ enabled: false });
  const busy = ref(false);

  const token = computed(() => userStore.token);

  async function refreshSettings() {
    const settingsResponse = await service.getSettings(token.value);
    settings.value = settingsResponse?.data ?? { enabled: false };
    userStore.setRBACEnabled(settings.value.enabled === true);

    return settings.value;
  }

  async function refreshRolesAndUsers() {
    busy.value = true;
    try {
      const [rolesResponse, usersResponse] = await Promise.all([
        service.getRoles(token.value),
        service.getUsers(token.value),
      ]);

      roles.value = rolesResponse?.data ?? [];
      users.value = usersResponse?.data ?? [];
    } finally {
      busy.value = false;
    }
  }

  async function refreshAll() {
    busy.value = true;
    try {
      const [rolesResponse, permissionsResponse, usersResponse] = await Promise.all([
        service.getRoles(token.value),
        service.getPermissions(token.value),
        service.getUsers(token.value),
      ]);

      roles.value = rolesResponse?.data ?? [];
      permissions.value = permissionsResponse?.data ?? [];
      userStore.syncDisabledPermissions(permissions.value);
      users.value = usersResponse?.data ?? [];
      await refreshSettings();

      const entries = await Promise.all(
        roles.value.map(async (role) => {
          const response = await service.getRolePermissions(token.value, role.id);
          return [role.id, response?.data ?? []];
        }),
      );

      rolePermissions.value = Object.fromEntries(entries);

      const tagEntries = await Promise.all(
        roles.value.map(async (role) => {
          const response = await service.getRoleTags(token.value, role.id);
          return [role.id, response?.data ?? []];
        }),
      );

      roleTags.value = Object.fromEntries(tagEntries);
    } finally {
      busy.value = false;
    }
  }

  async function createRole(payload) {
    await service.createRole(token.value, payload);
    await refreshAll();

    return roles.value.find((role) => role.name === payload.name) || null;
  }

  async function updateRole(id, payload) {
    await service.updateRole(token.value, id, payload);
    await refreshAll();
  }

  async function deleteRole(id) {
    await service.deleteRole(token.value, id);
    await refreshAll();
  }

  async function createPermission(payload) {
    await service.createPermission(token.value, payload);
    await refreshAll();
  }

  async function updatePermission(id, payload) {
    await service.updatePermission(token.value, id, payload);
    await refreshAll();
  }

  async function deletePermission(id) {
    await service.deletePermission(token.value, id);
    await refreshAll();
  }

  async function saveRolePermissions(roleId, permissionIDs) {
    await service.assignRolePermissions(token.value, roleId, permissionIDs);
    await refreshAll();
  }

  async function saveUserRoles(userId, roleIDs) {
    await service.assignUserRoles(token.value, userId, roleIDs);
    await refreshAll();
  }

  async function saveRoleTags(roleId, tagIDs) {
    await service.assignRoleTags(token.value, roleId, tagIDs);
    await refreshAll();
  }

  async function updateSettings(payload) {
    await service.updateSettings(token.value, payload);
    await refreshAll();
  }

  return {
    roles,
    permissions,
    users,
    rolePermissions,
    roleTags,
    settings,
    busy,
    refreshSettings,
    refreshRolesAndUsers,
    refreshAll,
    createRole,
    updateRole,
    deleteRole,
    createPermission,
    updatePermission,
    deletePermission,
    saveRolePermissions,
    saveUserRoles,
    saveRoleTags,
    updateSettings,
  };
});
